import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
    Autocomplete,
    Box,
    Button,
    Grid,
    IconButton,
    Switch,
    Tooltip,
    Typography,
    Chip,
    TextField
} from "@mui/material";
import { COURSE_CATEGORIES, SIM_AND_COURSE_FORMATS } from "../../config";
import MainCard from "../../components/cards/MainCard";
import { useTheme } from "@mui/material/styles";
import TenancySelectionComponent from "../../components/TenancySelectionComponent";
import { getCardColor } from "../../utils/colorUtils";
import HelpIcon from "@mui/icons-material/HelpOutline";
import TrimmedTextField from "../../components/TrimmedTextField";

CourseForm.propTypes = {
    initialData: PropTypes.object,
    onSave: PropTypes.func,
    mode: PropTypes.string
};

export default function CourseForm(props) {
    const { initialData, onSave, mode } = props;
    const theme = useTheme();
    const lightMode = theme.palette.mode === "light";
    const user = JSON.parse(localStorage.getItem("user"));

    const isAdmin = user.role === "Administrators";

    // Initialize courseData with backward compatibility
    const [courseData, setCourseData] = useState(() => ({
        ...initialData,
        // Convert single languageCode to array format for backward compatibility
        languageCodes: initialData.languageCodes
            ? (Array.isArray(initialData.languageCodes) ? initialData.languageCodes : [initialData.languageCodes])
            : (initialData.languageCode ? [initialData.languageCode] : [])
    }));

    const [loading, setLoading] = useState(false);
    const [isRandomSend, setIsRandomSend] = useState(initialData.randomSend ?? false);

    const courseLanguageCodeOptions = COURSE_CATEGORIES.split(",");
    const courseFormatOptions = SIM_AND_COURSE_FORMATS.split(",");
    // Topical category (distinct from the LMS/SCORM technical type). Drives the
    // reports-b "Course Type" column. Kept in sync with reports-b COURSE_TYPES.
    const courseTagOptions = ['Cybersecurity', 'Information Security', 'Workplace Practices', 'Policies', 'Compliance'];

    useEffect(() => {
        setCourseData(prevData => ({
            ...prevData,
            randomSend: isRandomSend
        }));
    }, [isRandomSend]);

    const handleRandomSendChange = (event) => {
        setIsRandomSend(event.target.checked);
    };

    const handleSave = () => {
        setLoading(true);
        onSave({ ...courseData, randomSend: Boolean(isRandomSend) });
        setLoading(false);
    };

    const handleSharedTenanciesChange = (tenancyIds) => {
        setCourseData(prevData => ({
            ...prevData,
            sharedTenancyIds: tenancyIds
        }));
    };

    const handlePublishToFamilyChange = (event) => {
        setCourseData(prevData => ({
            ...prevData,
            publishToFamily: event.target.checked
        }));
    };

    const handlePublishedChange = (event) => {
        setCourseData(prevData => ({
            ...prevData,
            published: event.target.checked
        }));
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <TrimmedTextField
                    label="Course Name"
                    value={courseData.courseName}
                    size="medium"
                    fullWidth
                    onChange={(e) => setCourseData({ ...courseData, courseName: e.target.value })}
                    sx={{ mb: 2 }}
                />
                <TrimmedTextField
                    label="Scored Out Of"
                    value={courseData.scoredOutOf}
                    size="medium"
                    fullWidth
                    onChange={(e) => setCourseData({ ...courseData, scoredOutOf: e.target.value })}
                    sx={{ mb: 2 }}
                />
                <TrimmedTextField
                    label="Course Content Path"
                    value={courseData.courseContentPath}
                    size="medium"
                    fullWidth
                    onChange={(e) => setCourseData({ ...courseData, courseContentPath: e.target.value })}
                    sx={{ mb: 2 }}
                    disabled={mode === "view"}
                />
                <Autocomplete
                    id="language-code"
                    size="medium"
                    multiple
                    selectOnFocus
                    clearOnBlur
                    handleHomeEndKeys
                    value={courseData.languageCodes || []}
                    options={courseLanguageCodeOptions}
                    sx={{ mb: 2 }}
                    onChange={(e, value) => setCourseData({ ...courseData, languageCodes: value || [] })}
                    renderInput={(params) => <TextField {...params} label="Languages" />}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                variant="outlined"
                                label={option}
                                {...getTagProps({ index })}
                                key={option}
                            />
                        ))
                    }
                />
                <Autocomplete
                    id="format-type"
                    size="medium"
                    selectOnFocus
                    clearOnBlur
                    handleHomeEndKeys
                    value={courseData.format}
                    options={courseFormatOptions}
                    sx={{ mb: 2 }}
                    onChange={(e, value) => setCourseData({ ...courseData, format: value })}
                    renderInput={(params) => <TrimmedTextField {...params} label="Format Type" />}
                />
                <Autocomplete
                    id="course-category"
                    size="medium"
                    selectOnFocus
                    clearOnBlur
                    handleHomeEndKeys
                    value={courseData.tag || null}
                    options={courseTagOptions}
                    sx={{ mb: 2 }}
                    onChange={(e, value) => setCourseData({ ...courseData, tag: value || "" })}
                    renderInput={(params) => (
                        <TrimmedTextField
                            {...params}
                            label="Course Category"
                            helperText="Topical category used for reporting (the reports Course Type breakdown)"
                        />
                    )}
                />

                {/* Visibility Card */}
                <MainCard
                    content={true}
                    boxShadow={true}
                    border={false}
                    sx={{
                        mb: 3,
                        bgcolor: getCardColor(lightMode, courseData.published)
                    }}
                >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography variant="h4">Course Visibility</Typography>
                            <Tooltip title="When enabled, the course will be visible to users according to sharing settings" placement="top">
                                <IconButton size="small">
                                    <HelpIcon sx={{ color: "secondary.main" }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Switch
                            checked={courseData.published}
                            onChange={handlePublishedChange}
                            inputProps={{ "aria-label": "course visibility" }}
                        />
                    </Box>
                    <Typography variant="caption">
                        {courseData.published
                            ? "The course is visible to users according to sharing settings"
                            : "The course is currently hidden from all users"}
                    </Typography>
                </MainCard>

                {/* Course Sharing Card */}
                <MainCard
                    content={true}
                    boxShadow={true}
                    border={false}
                    sx={{ mb: 3, bgcolor: getCardColor(lightMode) }}
                >
                    <Typography variant="h4" sx={{ mb: 2 }}>Course Sharing Settings</Typography>

                    {/* Tenancy Selection Component */}
                    <TenancySelectionComponent
                        selectedTenancies={courseData.sharedTenancyIds || []}
                        onChange={handleSharedTenanciesChange}
                        disabled={mode === "view"}
                    />

                    {/* Publish to Family Switch */}
                    <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 1,
                        mb: 1,
                        bgcolor: getCardColor(lightMode),
                        borderRadius: 1
                    }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography variant="body1">Share with Tenancy Family</Typography>
                            <Tooltip title="When enabled, this course will be accessible to all descendants of the selected tenancies" placement="top">
                                <IconButton size="small">
                                    <HelpIcon sx={{ color: "secondary.main" }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Switch
                            checked={courseData.publishToFamily || false}
                            onChange={handlePublishToFamilyChange}
                            inputProps={{ "aria-label": "publish to family" }}
                            disabled={courseData.sharedTenancyIds?.includes(0)}
                        />
                    </Box>

                    {courseData.sharedTenancyIds?.includes(0) && (
                        <Typography variant="caption">
                            "Family sharing" is disabled because the course is shared with all tenancies.
                        </Typography>
                    )}
                </MainCard>

                <Button
                    onClick={handleSave}
                    variant="contained"
                    color="warning"
                    fullWidth
                    disabled={loading}
                >
                    SAVE
                </Button>
            </Grid>
        </Grid>
    );
}